import { AutoEq } from '../MLJobPayloads'
import { AiUser } from './AiUser'
import { BaseMLModelJob } from './BaseMLModelJob'
import { Attachment } from './common'
import { MachineLearningModel } from './MachineLearningModel'
import { PitchMethod } from './MLTrainingJob'
import { User } from './User'
import { VoiceModel } from './VoiceModel'

export const DEFAULT_INFER_SAMPLE_RATE = 44100

export enum YoutubeConvertType {
  CLIP = 'clip',
  FULL = 'full'
}

// Premium jobs are guaranteed to have wav files.
export enum InferenceJobTier {
  FREE = 'free',
  PREMIUM = 'premium'
}

export enum InferenceJobShareStatus {
  PUBLIC = 'public',
  PRIVATE = 'private'
}

export enum TTSPitch {
  LOW = 0,
  MEDIUM = 6,
  HIGH = 12
}

export enum TTSLanguage {
  ENGLISH = 'en',
  GERMAN = 'de',
  SPANISH = 'es',
  FRENCH = 'fr',
  HINDI = 'hi',
  ITALIAN = 'it',
  JAPANESE = 'ja',
  KOREAN = 'ko',
  POLISH = 'pl',
  PORTUGUESE = 'pt',
  RUSSIAN = 'ru',
  TURKISH = 'tr',
  CHINESE = 'zh',
  ARABIC = 'ar'
}

export enum TTSSpeed {
  ULTRA_FAST = 'ultra_fast',
  FAST = 'fast',
  STANDARD = 'standard'
}

export enum MLInferenceJobType {
  TTS = 'tts',
  YOUTUBE = 'youtube',
  AUDIO = 'audio',
  DEMO = 'demo',
  PROJECT = 'project'
}

export const AUTOTUNE_KEYS = [
  'C:maj',
  'C#:maj',
  'D:maj',
  'D#:maj',
  'E:maj',
  'F:maj',
  'F#:maj',
  'G:maj',
  'G#:maj',
  'A:maj',
  'A#:maj',
  'B:maj',
  'C:min',
  'C#:min',
  'D:min',
  'D#:min',
  'E:min',
  'F:min',
  'F#:min',
  'G:min',
  'G#:min',
  'A:min',
  'A#:min',
  'B:min'
] as const

export type AutotuneKey = (typeof AUTOTUNE_KEYS)[number]

export type AutoEqPreset = { name: string; s3ReferenceFileUrl?: string }
export const AUTO_EQ_PRESETS: AutoEqPreset[] = [
  {
    name: 'Balanced',
    s3ReferenceFileUrl:
      'https://arpeggi-prod-private.s3.us-west-2.amazonaws.com/rvc/reference_audio/balanced_voiceEQ.wav'
  },
  {
    name: 'Warm',
    s3ReferenceFileUrl:
      'https://arpeggi-prod-private.s3.us-west-2.amazonaws.com/rvc/reference_audio/warmNoise2.wav'
  },
  {
    name: 'Bright',
    s3ReferenceFileUrl:
      'https://arpeggi-prod-private.s3.us-west-2.amazonaws.com/rvc/reference_audio/bright_vocals_referenceL.wav'
  },
  { name: 'Match Input', s3ReferenceFileUrl: undefined }
]

export interface PreProcessingEffects {
  NoiseGate?: {
    threshold_db: number // float = -100.0
    ratio: number // float = 10
    attack_ms: number // float = 1.0
    release_ms: number // float = 100.0
  }
  HighPassFilter?: {
    cutoff_frequency_hz: number // float = 50
  }
  LowPassFilter?: {
    cutoff_frequency_hz: number // float = 50
  }
  HighShelfFilter?: {
    cutoff_frequency_hz: number
    gain_db: number
    q: number
  }
  LowShelfFilter?: {
    cutoff_frequency_hz: number
    gain_db: number
    q: number
  }
  Compressor?: {
    threshold_db: number
    ratio: number
    attack_ms: number
    release_ms: number
  }
}

export interface PostProcessingEffects {
  Chorus?: {
    rate_hz: number
    depth: number
    centre_delay_ms: number
    feedback: number
    mix: number
  }
  Reverb?: {
    room_size: number // float = 0.5
    damping: number // float = 0.5
    wet_level: number // float = 0.33
    dry_level: number // float = 0.4
    width: number // float = 1.0
    freeze_mode: number // float = 0.0
  }
  Compressor?: {
    threshold_db: number
    ratio: number
    attack_ms: number
    release_ms: number
  }
  Delay?: {
    delay_seconds: number // float = 0.5
    feedback: number // float = 0.0
    mix: number // float = 0.5
  }
}

export interface InferPayloadMetadata {
  pitch?: number
  startTimeMs?: number
  endTimeMs?: number
  outputSampleRate?: number
  cuid?: string
  rms_mix_rate?: number
  strength?: number
  pre?: PreProcessingEffects
  post?: PostProcessingEffects
  autotune?: {
    correction_strength?: number
    kernel_size?: number
    smooth_window_size?: number
    key?: AutotuneKey
  }
  autoEq?: AutoEq
  pitch_drift?: {
    rate?: number
    depth?: number
  }
  normalize?: boolean
  removeSilence?: boolean // whether to split on silence before inference
  pitchMethod?: PitchMethod
  modelIsTraining?: boolean // whether the model was still training when the job was created
  autoPitch?: boolean // whether autoPitch was used on the FE
  projectId?: number
}

/**
 * NOTE: If this is something that will also effect MLChoirJob,
 * consider adding it to the InferPayloadMetadata interface.
 */
export interface MLInferenceJobMetadata extends InferPayloadMetadata {
  isRecordedAudio?: boolean
  inputTtsText?: string
  ttsData?: { language: TTSLanguage; speed: TTSSpeed }
  youtubeLink?: string
  splitInstrumental?: boolean // must be true for saveInstrumental and/or saveRecombinedAudio to be true
  saveInstrumental?: boolean
  saveRecombinedAudio?: boolean // uploads a file with the final processed audio + the backing track
  dereverb?: boolean
  debreath?: boolean
  splitHarmony?: boolean
  isTutorialConvert?: boolean
}

export enum ReservationType {
  ON_DEMAND = 'on-demand',
  SPOT = 'spot'
}

/**
 * The feedback for the MLInferenceJob output.
 */
export enum MLFeedback {
  GOOD = 'good',
  BAD = 'bad'
}

/** Used for both training and inference. */
export interface InstanceDetails {
  id: string
  type: string
  reservationType: ReservationType
  region: string
}

export interface MLInferenceJob extends BaseMLModelJob {
  inputFile?: Attachment
  originalInputFileName: string | null
  // only used for inference jobs that have an instrumental attached (e.g. youtube splitter)
  backingAudioFile?: Attachment
  recombinedAudioFile?: Attachment
  outputFile?: Attachment
  lossyOutputFile?: Attachment
  metadata: MLInferenceJobMetadata
  audioDurationMs?: number
  ttsCharacterCount?: number
  // Required only for jobs from the kits.io site
  machineLearningModelId?: number
  machineLearningModel?: MachineLearningModel
  voiceModelId?: number
  voiceModel?: VoiceModel
  user?: User
  projectId?: number
  // Required only for jobs from the kits.ai site.
  modelUrl?: string
  strapiMachineLearningModelId?: number
  aiUser?: AiUser
  type: MLInferenceJobType // type of inference job
  tier: string
  shareStatus: InferenceJobShareStatus
  audioArchived: boolean
  audioPlayed: boolean
  cacheKey?: string
}
